import React from 'react';
import { Mail, Sparkles, Brain, Search, ArrowUpRight, Folder } from 'lucide-react';

// Coded reinterpretation of the "email becomes a record" flow (crisp at any size).
// Four steps, each a small product card. Copy is fixed: no em/en dashes.

const STEPS = [
  { n: '1', label: 'Email arrives' },
  { n: '2', label: 'Archen™ reads it' },
  { n: '3', label: 'Filed to the project' },
  { n: '4', label: 'Months later' },
];

const CardShell = ({ icon: Icon, title, right, children }) => (
  <div className="flex flex-col rounded-2xl border border-[#E8DFD1] bg-gradient-to-b from-[#FDFAF5] to-[#F7F0E6] h-full overflow-hidden">
    <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-[#EAE1D3]">
      <span className="flex items-center gap-2 text-[#3A2F26] text-[13.5px] font-medium">
        <Icon size={15} className="text-[#7A5A2E]" strokeWidth={1.8} />
        {title}
      </span>
      {right && <span className="text-[#7A5A2E] text-[10px] tracking-[0.14em] uppercase">{right}</span>}
    </div>
    <div className="flex-1 px-5 py-4">{children}</div>
  </div>
);

const Pill = ({ children }) => (
  <div className="flex items-center gap-2 rounded-lg bg-white/70 border border-[#EAE1D3] px-3 py-2 text-[13px] text-[#3A2F26]">
    <span className="w-1.5 h-1.5 rounded-full bg-[#B79B6E]" />
    {children}
  </div>
);

const HowItWorksSteps = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
    {STEPS.map((s, i) => (
      <div key={s.n} className="flex flex-col">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-[#D8CBB6] text-[#7A5A2E] text-[11px] font-medium">{s.n}</span>
          <span className="text-[#7A5A2E] text-[11px] tracking-[0.16em] uppercase">{s.label}</span>
        </div>

        {i === 0 && (
          <CardShell icon={Mail} title="Inbox">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-[#DCE3EC] text-[#4B5A6B] text-[10.5px] font-semibold flex items-center justify-center">JR</span>
              <span className="text-[#2D2520] text-[13.5px] font-medium">Julia R.</span>
              <span className="ml-auto text-[#7A5A2E] text-[12px]">9:47 AM</span>
            </div>
            <p className="mt-3 text-[#4A3F34] text-[14px] leading-relaxed">
              Approving <span className="bg-[#EDE4D3] rounded px-1 py-0.5 text-[#3A2F26]">the kitchen layout</span> as drawn, go ahead.
            </p>
          </CardShell>
        )}

        {i === 1 && (
          <CardShell icon={Sparkles} title="Reading" right="Extracted">
            <div className="space-y-2">
              <Pill>Approval</Pill>
              <Pill>Kitchen layout</Pill>
              <Pill>May 14, 2026</Pill>
            </div>
          </CardShell>
        )}

        {i === 2 && (
          <CardShell icon={Brain} title="Project Memory">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 bg-[#E7EEE0] text-[#5B7247] text-[11.5px] rounded-full px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6E8B4F]" />Approval
              </span>
              <span className="text-[#7A5A2E] text-[12px]">May 14</span>
            </div>
            <p className="mt-3 text-[#1F1812] text-[15px] font-semibold">Client approval received</p>
            <p className="mt-1 text-[#7A6B58] text-[13px]">Kitchen layout, as drawn</p>
            <div className="mt-5 flex items-center gap-2 text-[#7A5A2E] text-[12.5px]">
              <Folder size={13} strokeWidth={1.8} /> 2307, Park Avenue
            </div>
          </CardShell>
        )}

        {i === 3 && (
          <CardShell icon={Search} title="Who approved the kitchen layout?">
            <p className="text-[#1F1812] text-[15px] leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: '19px' }}>
              Julia R. approved it on May 14, 2026.
            </p>
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-white/70 border border-[#EAE1D3] px-3 py-2.5">
              <Mail size={13} className="text-[#7A5A2E]" strokeWidth={1.8} />
              <span className="text-[#4A3F34] text-[13px]">Source: email thread</span>
              <ArrowUpRight size={14} className="ml-auto text-[#7A5A2E]" />
            </div>
          </CardShell>
        )}
      </div>
    ))}
  </div>
);

export default HowItWorksSteps;
